import React from "react";
import * as PropTypes from "prop-types";
import { css } from "@emotion/css";
import { periodPropType } from "../../propTypes";
import Divider from "@mui/material/Divider";
import LinearProgress from "@mui/material/LinearProgress";
import Select from "react-select";
import { withTranslation } from "react-i18next";
import PeriodSelector from "../PeriodSelector/PeriodSelector";
import ProgressButton from "../ProgressButton";

const classes = {
    actionButton: css`
        display: inline-flex;
        margin-top: 10px;
    `,
    divider: css`
        margin: 10px 0;
    `,
    instruction: css`
        margin: 10px 0;
        font-weight: bold;
    `,
    periodSelector: css`
        margin: 30px 20px 40px;
        z-index: -1;
    `,
    progress: css`
        margin: 15px 0 5px;
    `,
    select: css`
        margin-bottom: 10px;
    `,
    stepNumber: css`
        display: none;
    `,
};

const CompareRunsDrawerContent = ({ t, ...rest }) => {
    const onRun1Change = ({ value }) => {
        rest.onSelectRun(value);
    };

    const onRun2Change = ({ value }) => {
        rest.onSelectComparisonRun(value);
    };

    const onCountryPopulationChange1 = ({ value }) => {
        if (value >= 0) {
            rest.onSelectCountry1(value);
        } else {
            rest.onSelectRegion1(regions1.find((region) => region.value === value).label);
        }
    };

    const onCountryPopulationChange2 = ({ value }) => {
        if (value >= 0) {
            rest.onSelectCountry2(value);
        } else {
            rest.onSelectRegion2(regions2.find((region) => region.value === value).label);
        }
    };

    const runs = Object.entries(rest.runs).map(([id, run]) => ({
        value: id,
        label: run.name,
    }));

    const countries1 = Object.entries(rest.countries1)
        .map(([numericCode, country]) => ({
            value: parseInt(numericCode, 10),
            label: country.name,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    const countries2 = Object.entries(rest.countries2)
        .map(([numericCode, country]) => ({
            value: parseInt(numericCode, 10),
            label: country.name,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));

    const regions1 = rest.regions1.map((region, index) => ({
        value: -index - 1,
        label: region,
    }));
    const regions2 = rest.regions2.map((region, index) => ({
        value: -index - 1,
        label: region,
    }));

    const selectedRegion1 = regions1.find((region) => region.label === rest.selectedRegion1);
    const selectedRegion2 = regions2.find((region) => region.label === rest.selectedRegion2);
    const selectedRegionValue1 = selectedRegion1 && selectedRegion1.value;
    const selectedRegionValue2 = selectedRegion2 && selectedRegion2.value;
    const countriesAndRegions1 = countries1.concat(regions1);
    const countriesAndRegions2 = countries2.concat(regions2);
    const btnDisabled = !(
        (rest.selectedCountryNumericCode1 || rest.selectedRegion1) &&
        (rest.selectedCountryNumericCode2 || rest.selectedRegion2)
    );
    const selectedCountryPopulation1 = countriesAndRegions1.filter(
        ({ value }) => value === (selectedRegionValue1 || rest.selectedCountryNumericCode1)
    );
    const selectedCountryPopulation2 = countriesAndRegions2.filter(
        ({ value }) => value === (selectedRegionValue2 || rest.selectedCountryNumericCode2)
    );

    return (
        <>
            {rest.loadingData && (
                <div className={classes.progress}>
                    <LinearProgress />
                </div>
            )}

            <div className={classes.instruction}>
                <span className={classes.stepNumber}>1.</span> {t("Select runs to compare")}
            </div>

            <Select
                placeholder={t("Select run 1...")}
                options={runs}
                value={runs.filter(({ value }) => value === rest.selectedRunId)}
                className={classes.select}
                onChange={onRun1Change}
            />

            <Select
                placeholder={t("Select run 2...")}
                options={runs}
                value={runs.filter(({ value }) => value === rest.selectedComparisonRunId)}
                className={classes.select}
                onChange={onRun2Change}
            />

            <>
                <Divider className={classes.divider} />
                <div className={classes.instruction}>2. {t("Select country/region to view")}</div>

                <Select
                    placeholder={t("Select country/region 1...")}
                    options={countriesAndRegions1}
                    value={selectedCountryPopulation1}
                    className={classes.select}
                    onChange={onCountryPopulationChange1}
                />

                <Select
                    placeholder={t("Select country/region 2...")}
                    options={countriesAndRegions2}
                    value={selectedCountryPopulation2}
                    className={classes.select}
                    onChange={onCountryPopulationChange2}
                />

                <Divider className={classes.divider} />
            </>

            <ProgressButton
                variant="contained"
                color="primary"
                disabled={btnDisabled}
                loading={rest.loadingRuns}
                className={classes.actionButton}
                onClick={rest.onCompareRuns}
            >
                {t("Compare runs")}
            </ProgressButton>

            <Divider className={classes.divider} />

            <div className={classes.instruction}>{t("Select years for display/export of results")}</div>

            <div className={classes.periodSelector}>
                <PeriodSelector
                    period={rest.period}
                    onChange={rest.onChangePeriod}
                />
            </div>
        </>
    );
};

CompareRunsDrawerContent.propTypes = {
    loadingData: PropTypes.bool,
    runs: PropTypes.objectOf(
        PropTypes.shape({
            name: PropTypes.string.isRequired,
        })
    ),
    selectedRunId: PropTypes.string,
    selectedComparisonRunId: PropTypes.string,
    // countries: PropTypes.objectOf(
    //     PropTypes.shape({
    //         name: PropTypes.string.isRequired,
    //     })
    // ),
    // regions: PropTypes.arrayOf(PropTypes.string),
    selectedCountryNumericCode1: PropTypes.number,
    selectedCountryNumericCode2: PropTypes.number,
    selectedRegion1: PropTypes.string,
    selectedRegion2: PropTypes.string,
    period: periodPropType,
    loadingRuns: PropTypes.bool,
    onSelectRun: PropTypes.func.isRequired,
    onSelectComparisonRun: PropTypes.func.isRequired,
    onSelectCountry1: PropTypes.func.isRequired,
    onSelectRegion1: PropTypes.func.isRequired,
    onSelectCountry2: PropTypes.func.isRequired,
    onSelectRegion2: PropTypes.func.isRequired,
    onChangePeriod: PropTypes.func.isRequired,
    onCompareRuns: PropTypes.func.isRequired,
    countries1: PropTypes.objectOf(
        PropTypes.shape({
            name: PropTypes.string.isRequired,
        })
    ),
    countries2: PropTypes.objectOf(
        PropTypes.shape({
            name: PropTypes.string.isRequired,
        })
    ),
    regions1: PropTypes.arrayOf(PropTypes.string),
    regions2: PropTypes.arrayOf(PropTypes.string),
};

CompareRunsDrawerContent.defaultProps = {
    loadingData: false,
    runs: {},
    countries: {},
    regions: [],
    loadingRuns: false,
};

export default withTranslation()(CompareRunsDrawerContent);
